import { useState } from "react";
import { Globe, Check, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface Currency {
  code: string;
  symbol: string;
  flag: string;
}

interface Language {
  code: string;
  name: string;
  flag: string;
}

interface LanguageCurrencyModalProps {
  isOpen: boolean;
  onClose: () => void;
  currencies: Currency[];
  languages: Language[];
  selectedCurrency: Currency;
  selectedLanguage: Language;
  onCurrencyChange: (currency: Currency) => void;
  onLanguageChange: (language: Language) => void;
}

export const LanguageCurrencyModal = ({
  isOpen,
  onClose,
  currencies,
  languages,
  selectedCurrency,
  selectedLanguage,
  onCurrencyChange,
  onLanguageChange,
}: LanguageCurrencyModalProps) => {
  const [tempCurrency, setTempCurrency] = useState(selectedCurrency);
  const [tempLanguage, setTempLanguage] = useState(selectedLanguage);

  if (!isOpen) return null;

  const handleConfirm = () => {
    onCurrencyChange(tempCurrency);
    onLanguageChange(tempLanguage);
    onClose();
  };

  return (
    <>
      {/* Backdrop with blur */}
      <div 
        className="fixed inset-0 bg-foreground/40 backdrop-blur-md z-50 animate-fade-in"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div 
          className="bg-background rounded-2xl shadow-2xl w-full max-w-md pointer-events-auto animate-scale-in overflow-hidden border border-border"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="relative bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-6 pb-8">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-background/80 flex items-center justify-center hover:bg-background transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
            
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Globe className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-foreground">Nastavenia regiónu</h2>
                <p className="text-sm text-muted-foreground">Vyberte jazyk a menu</p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Language Selection */}
            <div>
              <label className="text-sm font-medium text-foreground mb-3 block">Jazyk</label>
              <div className="grid grid-cols-1 gap-2">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => setTempLanguage(lang)}
                    className={cn(
                      "flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left",
                      tempLanguage.code === lang.code
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50 hover:bg-secondary/50"
                    )}
                  >
                    <span className="text-2xl">{lang.flag}</span>
                    <span className="font-medium text-foreground flex-1">{lang.name}</span>
                    {tempLanguage.code === lang.code && (
                      <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                        <Check className="w-3 h-3 text-primary-foreground" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Currency Selection */}
            <div>
              <label className="text-sm font-medium text-foreground mb-3 block">Mena</label>
              <div className="grid grid-cols-2 gap-2">
                {currencies.map((curr) => (
                  <button
                    key={curr.code}
                    onClick={() => setTempCurrency(curr)}
                    className={cn(
                      "flex items-center gap-2 p-3 rounded-xl border-2 transition-all",
                      tempCurrency.code === curr.code
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50 hover:bg-secondary/50"
                    )}
                  >
                    <span className="text-lg">{curr.flag}</span>
                    <span className="font-bold text-foreground">{curr.symbol}</span>
                    <span className="text-muted-foreground text-sm">{curr.code}</span>
                    {tempCurrency.code === curr.code && (
                      <div className="w-4 h-4 rounded-full bg-primary flex items-center justify-center ml-auto">
                        <Check className="w-2.5 h-2.5 text-primary-foreground" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 bg-secondary/30 border-t border-border">
            <button
              onClick={handleConfirm}
              className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-bold hover:bg-primary/90 transition-colors"
            >
              Potvrdiť výber
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
